import { BaseError } from '@federa/const/base-error';
import { ERROR_CODES } from '@federa/const/error-codes';
import type { FlowStatus } from '@federa/types/flows';
import type { FlowRunStatus } from '@federa/types/primitives-status';

/** `completed` is NOT here: the engine's success terminals are `succeeded` and `responded`, so carrying it made the const a silent superset. It lives in `FLOW_RUN_ANALYTICS_STATUS`. */
export const FLOW_RUN_STATUS = {
	SUCCEEDED: 'succeeded',
	RESPONDED: 'responded',
	FAILED: 'failed',
	RUNNING: 'running',
	CANCELED: 'canceled',
	TIMED_OUT: 'timed_out',
} as const satisfies Record<string, FlowRunStatus>;

/**
 * The run terminals adjudicated as SUCCESS -- the value-SET twin of the per-value predicate `isFlowSuccessStatus`
 * (`utils/flow-engine/execution-result.ts`), for consumers that need the set rather than a predicate: SQL `IN (...)`
 * lists, aggregation buckets, bulk classification. Both members are real run terminals the engine assigns; the
 * analytics DISPLAY token `completed` is deliberately absent, because no emitter ever writes it as a run status.
 */
export const FLOW_RUN_SUCCESS_STATUSES = {
	SUCCEEDED: FLOW_RUN_STATUS.SUCCEEDED,
	RESPONDED: FLOW_RUN_STATUS.RESPONDED,
} as const satisfies Record<string, FlowRunStatus>;

/**
 * The run terminals counted as FAILURE. `timed_out` is a MEMBER: the engine adjudicates it non-success, and leaving it
 * uncounted made success+failure rates sum below 100%. `canceled` is deliberately ABSENT -- a deliberate cancel is its
 * own bucket, neither success nor failure, which is why this set plus `FLOW_RUN_SUCCESS_STATUSES` is not a partition of
 * `FLOW_RUN_STATUS`.
 */
export const FLOW_RUN_FAILURE_STATUSES = {
	FAILED: FLOW_RUN_STATUS.FAILED,
	TIMED_OUT: FLOW_RUN_STATUS.TIMED_OUT,
} as const satisfies Record<string, FlowRunStatus>;

/**
 * Every TERMINAL run status -- a run in one of these states never transitions again. Exactly
 * `FLOW_RUN_STATUS` minus `RUNNING`. SSOT for terminal-set consumers (the R2 persist gate in
 * flow-sync, the analytics execution-record vocabulary): derive Sets/subsets from this object,
 * never re-enumerate the members.
 */
export const TERMINAL_FLOW_RUN_STATUSES = {
	SUCCEEDED: FLOW_RUN_STATUS.SUCCEEDED,
	RESPONDED: FLOW_RUN_STATUS.RESPONDED,
	FAILED: FLOW_RUN_STATUS.FAILED,
	TIMED_OUT: FLOW_RUN_STATUS.TIMED_OUT,
	CANCELED: FLOW_RUN_STATUS.CANCELED,
} as const satisfies Record<string, FlowRunStatus>;

/**
 * Analytics / display status tokens, deliberately NOT run terminals -- which is why they are separate from
 * `FLOW_RUN_STATUS`. `unknown` is the display FALLBACK, since the R2 run-row `status` column is untyped.
 *
 * `INTERRUPTED` is the third, and it is a MEASURED state rather than a fallback: the span readers mint it for a row the
 * trace store holds with no end timestamp AND no status attribute -- a run or step cut mid-flight. It lives here for
 * the same reason `COMPLETED` does (no engine exit ever writes it) and it is deliberately not a `FLOW_RUN_STATUS`
 * member: `FLOW_RUN_STATUS` must keep excluding it, so no success/failure rollup can bucket a killed run.
 * `types/flows.ts` carries why it may never collapse into `UNKNOWN`.
 */
export const FLOW_RUN_ANALYTICS_STATUS = {
	COMPLETED: 'completed',
	INTERRUPTED: 'interrupted',
	UNKNOWN: 'unknown',
} as const satisfies Record<string, FlowStatus>;

export const FLOW_STATUS = {
	STARTED: 'started',
	COMPLETED: 'completed',
	FAILED: 'failed',
	PARTIAL: 'partial',
} as const;

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): a step's TERMINAL status from its execution-log entry's `success`.
 *
 * ONE SPELLING FOR TWO LANES. `mapExecutionLogToStepResults` (`utils/flow-engine/step-results-map.ts`) derives it for
 * the published step ROW's `status` (the local run journal's step rows), and `stepSpanOutcomeAttributes` (`utils/flow-engine/step-span-attributes.ts`)
 * derives it for the span's `flow.step.status` attribute. A reader joining a step span to its step row on
 * `(run id, node id)` would have no way to tell a genuine disagreement from two subtly different ternaries, so the
 * derivation lives here and both lanes call it.
 *
 * `success` is widened to `unknown` on purpose: the log entry is a `Record<string, unknown>`, and the ONLY value that
 * means failure is a literal `false`. An absent, undefined or non-boolean field means the writer never claimed failure
 * -- which is COMPLETED, and is the behaviour both call sites had before this was extracted.
 */
export function flowStepStatusForSuccess(success: unknown): FlowStatus {
	return success === false ? FLOW_STATUS.FAILED : FLOW_STATUS.COMPLETED;
}

/** The executor's three runaway-guard ceilings, grouped (the `HTTP_STATUS` shape) because they are ORDERED against each
 * other -- subflow depth below branch iterations below whole-run iterations -- and that ordering is what the
 * `flow-engine-status.test.ts` pins assert. Three flat exports let one be raised without the other two in view. */
export const FLOW_MAX = {
	/** Subflow nesting depth at which `createExecuteFlow` throws FLOW_SUBFLOW_DEPTH_EXCEEDED. */
	SUBFLOW_DEPTH: 10,
	/** Whole-run node-visit ceiling before the executor fails the run. */
	ITERATIONS: 100,
	/** Per-branch node-visit ceiling inside a split branch runner. */
	BRANCH_ITERATIONS: 50,
} as const;

/**
 * @runtime-derived-ssot-helper (const-SSOT derivation): the ONE check against {@link FLOW_MAX}'s `SUBFLOW_DEPTH`,
 * homed beside the bound it enforces. `createExecuteFlow` calls it BEFORE any run state is minted, so a runaway
 * subflow cycle costs no session. It reads only const vocabulary, so the const floor is its home.
 */
export function assertSubflowDepth(flowDef: { readonly id: string }, depth: number): void {
	if (depth < FLOW_MAX.SUBFLOW_DEPTH) return;
	throw new BaseError({
		code: ERROR_CODES.FLOW_SUBFLOW_DEPTH_EXCEEDED,
		message: `Subflow depth limit exceeded (max ${FLOW_MAX.SUBFLOW_DEPTH}). Flow '${flowDef.id}' at depth ${depth}.`,
		details: { flowId: flowDef.id, depth, maxDepth: FLOW_MAX.SUBFLOW_DEPTH },
	});
}

export const TEAM_TYPES = {
	FINANCE: 'Finance',
	OPERATIONS: 'Operations',
	SUCCESS: 'Success Manager',
} as const;

export const TEAM_ROLES = {
	ADMIN: 'admin',
	APPROVER: 'approver',
	MEMBER: 'member',
	VIEWER: 'viewer',
} as const;
